import { useCallback, useContext, useEffect, useState } from "react";
import { Alert, Button, Col, Form, Row, Spinner } from "react-bootstrap";
import { useParams } from "react-router-dom";
import axios from "axios";
import { AuthContext } from "../../context/AuthContext";
import CountryCodeSelect from "../CountryCodeSelect";
import { generateSiteCode } from "../../utils/siteCodeGenerator";
import SuccessModal from "./SuccessModal";
import { useNotificationModal } from "../../hooks/useNotificationModal";
import CountryDropdown from "../CountryDropdown";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

// Fetch sites (plants, branches) from API
const fetchSitesByType = async (companyCode, siteType) => {
  try {
    const response = await axios.get(
      `${BASE_URL}/api/admin/sites/get-all-sites-based-on-type`,
      {
        params: {
          company_code: companyCode,
          type: siteType,
        },
      },
    );
    return response.data.items || [];
  } catch (error) {
    console.error(`Error fetching ${siteType}s:`, error);
    return [];
  }
};

export default function AddEditSitesModal({
  onSuccess,
  editData = null,
  editId = null,
  type = "",
} = {}) {
  const { userData } = useContext(AuthContext);
  const { id: routeId } = useParams();
  const id = editId || routeId;
  const isUpdateMode = Boolean(editData || id);

  // Get display name based on type
  const getDisplayName = () => {
    if (type === "branch") return "Branch";
    if (type === "plant") return "Plant";
  };

  const displayName = getDisplayName();

  const {
    showSuccessModal,
    successMessage,
    successTitle,
    showErrorModal,
    errorMessage,
    errorTitle,
    showSuccess,
    hideSuccess,
    hideError,
  } = useNotificationModal();

  const [formData, setFormData] = useState({
    site_name: "",
    address_line_1: "",
    address_line_2: "",
    city: "",
    state: "",
    country: "",
    phone_number: "",
    countryCode: "+94",
    email: "",
    description: "",
    plant_id: "",
    site_code: "",
  });
  const [errorMsg, setErrorMsg] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [phoneError] = useState("");
  const [plants, setPlants] = useState([]);
  const [plantsLoading, setPlantsLoading] = useState(false);
  const [autoGeneratedCode, setAutoGeneratedCode] = useState("");
  const [, setBranches] = useState([]);
  const [, setBranchesLoading] = useState(false);
  const [isManuallyEdited, setIsManuallyEdited] = useState(false);

  //Display existing data in edit mode and load plants/branches
  useEffect(() => {
    if (editData) {
      setFormData({
        site_name: editData.site_name || "",
        address_line_1: editData.address_line_1 || "",
        address_line_2: editData.address_line_2 || "",
        city: editData.city || "",
        state: editData.state || "",
        country: editData.country || "",
        phone_number: editData.phone || "",
        countryCode: editData.country_code || "+94",
        email: editData.email || "",
        description: editData.description || "",
        plant_id: editData.plant_id || "",
        site_id: editData.site_id || "",
        site_code: editData.site_code || "",
      });
      // Mark as manually edited in edit mode to preserve existing site code
      setIsManuallyEdited(true);
    } else {
      // Reset manual edit flag for create mode
      setIsManuallyEdited(false);
    }

    // Load plants for branch types
    if (type === "branch" && userData?.company_code) {
      loadPlants();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editData, type, userData]);

  // Load branches when a plant is selected (for branch type)
  useEffect(() => {
    if (type === "branch" && formData.plant_id && userData?.company_code) {
      loadBranches();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.plant_id, type, userData]);

  const loadPlants = async () => {
    setPlantsLoading(true);
    try {
      const plantsList = await fetchSitesByType(userData.company_code, "plant");
      setPlants(plantsList);
    } catch (error) {
      console.error("Error loading plants:", error);
      setPlants([]);
    } finally {
      setPlantsLoading(false);
    }
  };

  const loadBranches = async () => {
    setBranchesLoading(true);
    try {
      const branchesList = await fetchSitesByType(
        userData.company_code,
        "branch",
      );
      setBranches(branchesList);
    } catch (error) {
      console.error("Error loading branches:", error);
      setBranches([]);
    } finally {
      setBranchesLoading(false);
    }
  };

  // Auto-generate site code when site name or parent sites change
  const updateSiteCode = useCallback(() => {
    // Skip if in edit mode or site name is empty
    if (!formData.site_name || !userData?.company_code) {
      setAutoGeneratedCode("");
      return;
    }

    let plantName = "";

    // For branch type, fetch plant name
    if (type === "branch" && formData.plant_id) {
      const selectedPlant = plants.find(
        (p) => p.site_id === parseInt(formData.plant_id),
      );
      if (selectedPlant) {
        plantName = selectedPlant.site_name;
      }
    }

    // Generate code based on type
    const generatedCode = generateSiteCode(
      type,
      formData.site_name,
      userData.company_code,
      plantName,
    );

    setAutoGeneratedCode(generatedCode);

    // Only auto-update site_code if not manually edited
    if (!isManuallyEdited) {
      setFormData((prev) => ({ ...prev, site_code: generatedCode }));
    }
  }, [
    formData.site_name,
    formData.plant_id,
    type,
    plants,
    userData,
    isManuallyEdited,
  ]);

  // Update site code whenever site name or parent sites change
  useEffect(() => {
    updateSiteCode();
  }, [updateSiteCode]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // If user manually edits the site_code, mark it as manually edited
    if (name === "site_code") {
      setIsManuallyEdited(true);
      setFormData((prev) => ({ ...prev, [name]: value.toUpperCase() }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const createBranch = async () => {
    try {
      // Validate site code
      if (!formData.site_code || formData.site_code.trim() === "") {
        setErrorMsg("Site code is required");
        setTimeout(() => setErrorMsg(""), 3000);
        return;
      }

      const requestBody = {
        site_name: formData.site_name,
        address_line_1: formData.address_line_1,
        address_line_2: formData.address_line_2,
        city: formData.city,
        state: formData.state,
        country: formData.country,
        country_code: formData.countryCode,
        phone: formData.phone_number,
        email: formData.email,
        description: formData.description,
        site_code: formData.site_code.trim(), // auto-generated or manually edited
        company_code: userData.company_code,
      };

      // Add both plant_id for branch type
      if (type === "branch") {
        requestBody.plant_id = formData.plant_id;
      }

      const response = await axios.post(
        `${BASE_URL}/api/admin/sites/create-site`,
        requestBody,
        {
          params: { company_code: userData.company_code, type: type },
        },
      );

      if (response.data.success) {
        showSuccess(
          `${displayName} created successfully! Site Code: ${formData.site_code}`,
          "Success",
        );
        setTimeout(() => {
          hideSuccess();
          if (onSuccess) onSuccess();
        }, 2000);
      } else {
        throw new Error(response.data.message || `Failed to create ${type}`);
      }
    } catch (error) {
      throw error;
    }
  };
  const updateBranch = async () => {
    try {
      const updateData = {
        site_name: formData.site_name,
        address_line_1: formData.address_line_1,
        address_line_2: formData.address_line_2,
        city: formData.city,
        state: formData.state,
        country: formData.country,
        country_code: formData.countryCode,
        phone: formData.phone_number,
        email: formData.email,
        description: formData.description,
        site_code: formData.site_code.trim(),
        company_code: userData.company_code,
        type: type,
      };

      const response = await axios.put(
        `${BASE_URL}/api/admin/sites/update-site/${editData.site_id}`,
        updateData,
      );

      if (response.data.success) {
        showSuccess(`${displayName} updated successfully!`, "Success");
        setTimeout(() => {
          hideSuccess();
          if (onSuccess) onSuccess();
        }, 2000);
      } else {
        throw new Error(response.data.message || `Failed to update ${type}`);
      }
    } catch (error) {
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMsg("");

    // Validate plant selection for branch type
    if (type === "branch") {
      if (!formData.plant_id) {
        setErrorMsg("Please select a plant");
        setTimeout(() => setErrorMsg(""), 3000);
        setIsLoading(false);
        return;
      }
    }

    // Validate phone number before submitting
    if (formData.phone_number) {
      const cleanPhone = formData.phone_number.replace(/\D/g, "");
      if (cleanPhone.length < 7 || cleanPhone.length > 15) {
        setErrorMsg("Phone number must be 7-15 digits");
        setTimeout(() => setErrorMsg(""), 3000);
        setIsLoading(false);
        return;
      }
    }

    // Validate country code
    const countryCode = formData.countryCode || "";
    const cleanCountryCode = countryCode.replace(/\D/g, "");
    if (
      !countryCode.startsWith("+") ||
      cleanCountryCode.length < 1 ||
      cleanCountryCode.length > 4
    ) {
      setErrorMsg("Country code must start with + and be 1-4 digits");
      setTimeout(() => setErrorMsg(""), 3000);
      setIsLoading(false);
      return;
    }

    try {
      if (isUpdateMode) {
        await updateBranch();
      } else {
        await createBranch();
      }
    } catch (error) {
      console.error(`${isUpdateMode ? "Update" : "Create"} error:`, error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        `Something went wrong. Please try again.`;
      setErrorMsg(errorMessage);

      // Clear error message after 3 seconds
      setTimeout(() => setErrorMsg(""), 3000);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <SuccessModal
        show={showSuccessModal}
        onHide={hideSuccess}
        title={successTitle}
        message={successMessage}
        variant="success"
      />
      <SuccessModal
        show={showErrorModal}
        onHide={hideError}
        title={errorTitle}
        message={errorMessage}
        variant="error"
      />
      {errorMsg && (
        <Alert variant="danger" className="text-center">
          {errorMsg}
        </Alert>
      )}

      <Form className="register-form" onSubmit={handleSubmit}>
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>{displayName} Name*</Form.Label>
              <Form.Control
                type="text"
                name="site_name"
                value={formData.site_name}
                onChange={handleChange}
                placeholder={`Enter ${type} name`}
                required
              />
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Site Code*</Form.Label>
              <div className="d-flex gap-2 align-items-center">
                <Form.Control
                  type="text"
                  name="site_code"
                  value={formData.site_code}
                  onChange={handleChange}
                  placeholder="Site code will be auto-generated"
                  style={{ textTransform: "uppercase" }}
                  required
                />
                {autoGeneratedCode &&
                  formData.site_code !== autoGeneratedCode && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => {
                        setFormData((prev) => ({
                          ...prev,
                          site_code: autoGeneratedCode.toUpperCase(),
                        }));
                        setIsManuallyEdited(false);
                      }}
                      title="Reset to auto-generated code"
                    >
                      Reset
                    </button>
                  )}
              </div>
              <Form.Text className="text-muted">
                {autoGeneratedCode
                  ? `Auto-generated: ${autoGeneratedCode}`
                  : "Site code will be generated based on site name"}
              </Form.Text>
            </Form.Group>
          </Col>
        </Row>

        {/* Plant Selection for Branch types */}
        {type === "branch" && (
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Plant*</Form.Label>
                <Form.Select
                  name="plant_id"
                  value={formData.plant_id}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      plant_id: e.target.value,
                    }))
                  }
                  disabled={plantsLoading}
                  required
                >
                  <option value="">
                    {plantsLoading ? "Loading plants..." : "Select a plant"}
                  </option>
                  {plants.map((plant) => (
                    <option key={plant.site_id} value={plant.site_id}>
                      {plant.site_name} - {plant.city}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        )}

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Phone Number</Form.Label>
              <div className="d-flex gap-2">
                <CountryCodeSelect
                  value={formData.countryCode}
                  onChange={(code) =>
                    setFormData((prev) => ({ ...prev, countryCode: code }))
                  }
                  placeholder="Code"
                  isRequired={false}
                  width="100px"
                />
                <Form.Control
                  type="text"
                  name="phone_number"
                  value={formData.phone_number}
                  onChange={(e) => {
                    // Only allow digits
                    const value = e.target.value.replace(/\D/g, "");
                    setFormData((prev) => ({ ...prev, phone_number: value }));
                  }}
                  placeholder="Enter phone number"
                  style={{
                    flex: 1,
                    height: "48px",
                    fontSize: "1rem",
                    borderRadius: "12px",
                    backgroundColor: "#f7fafc",
                    border: "2px solid #e2e8f0",
                  }}
                />
              </div>
              <Form.Text className="text-muted">
                Enter phone number with country code (e.g., +94 for Sri Lanka)
              </Form.Text>
              {phoneError && (
                <div className="invalid-feedback d-block">{phoneError}</div>
              )}
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder={`Enter ${type} email`}
              />
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>
                Address Line 1 <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="address_line_1"
                value={formData.address_line_1}
                onChange={handleChange}
                placeholder="Address Line 1"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Address Line 2</Form.Label>
              <Form.Control
                type="text"
                name="address_line_2"
                value={formData.address_line_2}
                onChange={handleChange}
                placeholder="Address Line 2"
              />
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>
                City <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                placeholder="City"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>State </Form.Label>
              <Form.Control
                type="text"
                name="state"
                value={formData.state}
                onChange={handleChange}
                placeholder="State"
              />
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label> Country </Form.Label>
              <CountryDropdown
                value={formData.country || ""}
                onChange={(country) =>
                  setFormData((prev) => ({ ...prev, country: country }))
                }
                placeholder="Select country"
              />
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                type="text"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Additional description"
              />
            </Form.Group>
          </Col>
        </Row>

        <div className="text-center">
          <Button type="submit" className="register-btn" disabled={isLoading}>
            {isLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                {isUpdateMode
                  ? `Updating ${displayName}...`
                  : `Creating ${displayName}...`}
              </>
            ) : isUpdateMode ? (
              `Update ${displayName}`
            ) : (
              `Create ${displayName}`
            )}
          </Button>
        </div>
      </Form>
    </>
  );
}
